import React from "react";
import { Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import {
  DevToolItem,
  FeatureToggle,
} from "@components/dev-features-dialog/features";
import { DevPageItem } from "@components/dev-features-dialog/dev-pages";

type DevFeaturesTableItem = DevToolItem | FeatureToggle | DevPageItem;

type DevFeaturesTableProps<T extends DevFeaturesTableItem> = {
  /** Title of the table section */
  title: string;

  /** Items to be displayed in the table */
  items: T[];

  /** Renders what the user can do with an item, e.g. a switch or a button */
  renderAction: (item: T) => React.ReactNode;
};

/** A row of the table, which keeps the item it shows for the cells to render */
type DevFeaturesTableRow<T extends DevFeaturesTableItem> = {
  id: number;
  item: T;
};

/**
 * Render a table component for the development features or tools.
 */
export function DevFeaturesTable<T extends DevFeaturesTableItem>({
  title,
  items,
  renderAction,
}: DevFeaturesTableProps<T>): React.JSX.Element {
  const columns: GridColDef<DevFeaturesTableRow<T>>[] = [
    {
      field: "label",
      headerName: "Name",
      flex: 1,
      renderCell: ({ row }) => (
        <Text variant="heading12">{row.item.label}</Text>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: ({ row }) => (
        <Text variant="helpText">{row.item.description}</Text>
      ),
    },
    {
      field: "action",
      headerName: "",
      width: 120,
      align: "center",
      renderCell: ({ row }) => renderAction(row.item),
    },
  ];

  return (
    <Box>
      <Text variant="heading16" sx={{ display: "flex", mb: 1 }}>
        {title}
      </Text>
      <DataGridPro
        // The grid sits in a Box with no height of its own, so it takes the height of its rows
        autoHeight
        rows={items.map((item, index) => ({ id: index, item }))}
        columns={columns}
        getRowHeight={() => "auto"}
        disableColumnMenu
        disableColumnSorting
        disableColumnResize
        disableRowSelectionOnClick
        hideFooter
        sx={{
          // Rows grow with their content, so the cells have to centre it themselves
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            py: 1,
          },
        }}
      />
    </Box>
  );
}
